import React from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useLocation, useMatches } from "react-router-dom";

import { isOutsideSafeReadOnlyAllowlistedHref } from "@/features/navigation/appRouteManifest";
import { useRuntimeState } from "@/features/runtime";

type OutsideSafeRouteHandle = {
  outsideSafeReadOnlyAccess?: "allowlisted" | "blocked";
};

function normalizePathname(pathname: string): string {
  if (pathname === "/") {
    return pathname;
  }
  return pathname.replace(/\/+$/, "");
}

function getLeafOutsideSafeRouteAccess(
  matches: ReturnType<typeof useMatches>
): OutsideSafeRouteHandle["outsideSafeReadOnlyAccess"] {
  const leafMatch = matches[matches.length - 1];
  const handle = leafMatch?.handle as OutsideSafeRouteHandle | undefined;
  return handle?.outsideSafeReadOnlyAccess;
}

export function MainnetOnlyGate() {
  const { t } = useTranslation();
  const runtime = useRuntimeState();
  const location = useLocation();
  const matches = useMatches();
  const normalizedPathname = normalizePathname(location.pathname || "/");
  const leafOutsideSafeRouteAccess = getLeafOutsideSafeRouteAccess(matches);
  const outsideSafeRouteBlocked =
    runtime.mode === "OUTSIDE_SAFE_READ_ONLY" &&
    (leafOutsideSafeRouteAccess !== "allowlisted" ||
      !isOutsideSafeReadOnlyAllowlistedHref(normalizedPathname));
  const chainStatus = runtime.posture.safe.chainStatus.toLowerCase();
  const nonceStatus = runtime.posture.safe.nonceStatus.toLowerCase();

  if (runtime.mode === "INITIALIZING") {
    // One calm card carries the write-lock message while the handshake
    // resolves; chain/queue diagnostics stay off layer 1 (title attr only).
    return (
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        title={t("app.mainnetOnly.runtimeGate.technicalStatusTitle", {
          chainStatus,
          nonceStatus
        })}
        className="tw:rounded-xl tw:border tw:border-iron-800 tw:bg-iron-900 tw:p-4 tw:text-sm tw:text-iron-200"
      >
        <div>{t("app.mainnetOnly.runtimeGate.checkingContext")}</div>
        <div className="tw:mt-2 tw:text-xs tw:text-iron-400">
          {t("app.mainnetOnly.runtimeGate.failClosedContext")}
        </div>
        <div aria-hidden="true" className="tw:mt-4 tw:space-y-2">
          <div className="tw:h-3 tw:w-2/3 tw:animate-pulse tw:rounded-sm tw:bg-iron-800" />
          <div className="tw:h-3 tw:w-1/2 tw:animate-pulse tw:rounded-sm tw:bg-iron-800" />
          <div className="tw:h-3 tw:w-3/5 tw:animate-pulse tw:rounded-sm tw:bg-iron-800" />
        </div>
      </div>
    );
  }

  if (outsideSafeRouteBlocked) {
    const blockedRoute = encodeURIComponent(normalizedPathname);
    return (
      <Navigate
        to={`/unsupported-network?reason=outside-safe-route&blocked=${blockedRoute}`}
        replace
      />
    );
  }

  if (!runtime.capabilities.canRenderOperationalRoutes) {
    return <Navigate to="/unsupported-network" replace />;
  }

  return <Outlet />;
}
